/**
 * authStore - client-side auth state.
 *
 * Holds the authenticated user, derived permission/assignment lookups, and
 * a status flag for the initial `/auth/me` hydration. The store is a thin
 * cache around the AuthClient — server is the source of truth.
 */
import type {
  AllowedModel,
  AuthenticatedUser,
  GlobalPermission,
  ManagedAgentId,
  ModelSelection,
  ProjectId,
  ProjectPermission,
  ProviderKind,
} from "@taskin-code/contracts";
import { create } from "zustand";

import { AuthClient } from "./AuthClient";

export type AuthStatus = "idle" | "loading" | "authenticated" | "unauthenticated";

// Compact key for fast set membership checks ("provider:slug"). The keys are
// only ever produced/consumed by hooks here, so they don't escape this module.
const modelKey = (provider: ProviderKind, slug: string): string => `${provider}:${slug}`;

interface AuthState {
  readonly status: AuthStatus;
  readonly user: AuthenticatedUser | null;
  readonly globalPermissions: ReadonlySet<GlobalPermission>;
  readonly projectAssignments: ReadonlyMap<ProjectId, ReadonlySet<ProjectPermission>>;
  readonly allowedModels: ReadonlyArray<AllowedModel>;
  readonly allowedModelKeys: ReadonlySet<string>;
  readonly defaultModel: AllowedModel | null;
  /**
   * Empty means "no personal policy", not "no agents" — team scoping decides in
   * that case, and the server has already applied it to whatever the agent
   * directory returned. Only a non-empty set filters anything client-side.
   */
  readonly allowedAgentIds: ReadonlySet<ManagedAgentId>;
  readonly defaultAgentId: ManagedAgentId | null;
  readonly hydrate: () => Promise<void>;
  readonly applyUser: (user: AuthenticatedUser | null) => void;
  readonly logout: () => Promise<void>;
}

const buildDerived = (user: AuthenticatedUser | null) => {
  if (!user) {
    return {
      globalPermissions: new Set<GlobalPermission>(),
      projectAssignments: new Map<ProjectId, ReadonlySet<ProjectPermission>>(),
      allowedModels: [] as ReadonlyArray<AllowedModel>,
      allowedModelKeys: new Set<string>(),
      defaultModel: null as AllowedModel | null,
      allowedAgentIds: new Set<ManagedAgentId>(),
      defaultAgentId: null as ManagedAgentId | null,
    };
  }
  const globalPermissions = new Set<GlobalPermission>(user.permissions);
  const projectAssignments = new Map<ProjectId, ReadonlySet<ProjectPermission>>();
  for (const assignment of user.projectAssignments) {
    projectAssignments.set(assignment.projectId, new Set(assignment.permissions));
  }
  const allowedModels = user.allowedModels ?? [];
  const allowedModelKeys = new Set<string>();
  for (const m of allowedModels) {
    allowedModelKeys.add(modelKey(m.provider, m.model));
  }
  return {
    globalPermissions,
    projectAssignments,
    allowedModels,
    allowedModelKeys,
    defaultModel: user.defaultModel ?? null,
    allowedAgentIds: new Set<ManagedAgentId>(user.allowedAgentIds ?? []),
    defaultAgentId: user.defaultAgentId ?? null,
  };
};

export const useAuthStore = create<AuthState>((set, get) => ({
  status: "idle",
  user: null,
  globalPermissions: new Set<GlobalPermission>(),
  projectAssignments: new Map<ProjectId, ReadonlySet<ProjectPermission>>(),
  allowedModels: [],
  allowedModelKeys: new Set<string>(),
  defaultModel: null,
  allowedAgentIds: new Set<ManagedAgentId>(),
  defaultAgentId: null,

  hydrate: async () => {
    set({ status: "loading" });
    try {
      const user = await AuthClient.me();
      const derived = buildDerived(user);
      set({
        status: user ? "authenticated" : "unauthenticated",
        user,
        ...derived,
      });
    } catch {
      set({
        status: "unauthenticated",
        user: null,
        globalPermissions: new Set(),
        projectAssignments: new Map(),
        allowedModels: [],
        allowedModelKeys: new Set(),
        defaultModel: null,
        allowedAgentIds: new Set(),
        defaultAgentId: null,
      });
    }
  },

  applyUser: (user) => {
    const derived = buildDerived(user);
    set({
      status: user ? "authenticated" : "unauthenticated",
      user,
      ...derived,
    });
  },

  logout: async () => {
    try {
      await AuthClient.logout();
    } finally {
      get().applyUser(null);
    }
  },
}));

export const hasGlobalPermission = (
  permissions: ReadonlySet<GlobalPermission>,
  permission: GlobalPermission,
): boolean => permissions.has(permission);

export const hasProjectPermission = (
  assignments: ReadonlyMap<ProjectId, ReadonlySet<ProjectPermission>>,
  projectId: ProjectId,
  permission: ProjectPermission,
): boolean => assignments.get(projectId)?.has(permission) ?? false;

// Hooks: render-time permission checks. The backend remains the security
// boundary — these only drive whether to render UI surfaces.

export const useHasGlobalPermission = (permission: GlobalPermission): boolean =>
  useAuthStore((state) => state.globalPermissions.has(permission));

export const useHasProjectPermission = (
  projectId: ProjectId | null | undefined,
  permission: ProjectPermission,
): boolean =>
  useAuthStore((state) => {
    if (!projectId) return false;
    return state.projectAssignments.get(projectId)?.has(permission) ?? false;
  });

// Returns true when the user has `permission` on at least one assigned project.
// Useful for surfaces that span projects (e.g. picker entry points).
export const useHasAnyProjectPermission = (permission: ProjectPermission): boolean =>
  useAuthStore((state) => {
    for (const grants of state.projectAssignments.values()) {
      if (grants.has(permission)) return true;
    }
    return false;
  });

// Model policy hooks. The backend rejects forbidden models on dispatch; these
// drive UI surfaces (model picker filtering, badges, etc.).

export const useAllowedModels = (): ReadonlyArray<AllowedModel> =>
  useAuthStore((state) => state.allowedModels);

export const useDefaultModel = (): AllowedModel | null =>
  useAuthStore((state) => state.defaultModel);

export const useIsModelAllowed = (
  selection: ModelSelection | { provider: ProviderKind; model: string } | null | undefined,
): boolean =>
  useAuthStore((state) => {
    if (!selection) return false;
    return state.allowedModelKeys.has(modelKey(selection.provider, selection.model));
  });

// Agent policy hooks, with the same division of labour: the backend rejects a
// forbidden agent on dispatch, these only decide what the UI offers and flags.

export const useAllowedAgentIds = (): ReadonlySet<ManagedAgentId> =>
  useAuthStore((state) => state.allowedAgentIds);

export const useDefaultAgentId = (): ManagedAgentId | null =>
  useAuthStore((state) => state.defaultAgentId);

/**
 * An absent agent id answers `true`: a thread running a plain model is not
 * running a forbidden agent, and treating "no agent" as a violation would put a
 * warning badge on every ordinary conversation.
 */
export const useIsAgentAllowed = (agentId: ManagedAgentId | null | undefined): boolean =>
  useAuthStore((state) => {
    if (!agentId) return true;
    if (state.allowedAgentIds.size === 0) return true;
    return state.allowedAgentIds.has(agentId);
  });
